import { useMutation as useBaseMutation, useQuery as useBaseQuery, useLazyQuery as useBaseLazyQuery } from '@apollo/client'
import { useState } from 'react'
import { useLocalStorage, useOnlineUtils } from './offline'
import { nanoid } from 'nanoid'
import { cloneDeep } from 'lodash'

const createID = () => nanoid(10)

const useOnlineQuery = (query, options) => useBaseQuery(query, options)
const useMutation = (query, options) => useBaseMutation(query, options)


const useLazyQuery = (query, options) => {
    let originalOnCompleted = options.onCompleted
    options.onCompleted = (data) => {
        originalOnCompleted && originalOnCompleted(cloneDeep(data))
    }

    let result = useBaseLazyQuery(query, options)
    return result
}

const useQuery = (query, options) => {
    let result =  useBaseQuery(query, options)
    let originalOnCompleted = options.onCompleted
    options.onCompleted = (data) => {
        originalOnCompleted && originalOnCompleted(cloneDeep(data))
    }

    return cloneDeep(result)
}


const useOfflineQuery = (query, options) => {
    const { localState } = useLocalStorage()
    const { isOnline } = useOnlineUtils()
    const [previousData, setPreviousData] = useState(undefined)

    let originalOnCompleted = options.onCompleted
    options.onCompleted = (data) => {
        originalOnCompleted && originalOnCompleted(cloneDeep(data))
    }

    let result = useBaseQuery(query, options)

    // store original refetch
    if (!result.originalRefetch) {
        result.originalRefetch = result.refetch
    }

    // replace refetch with custom offline/online refetch mapping
    result.refetch = async (variables) => {
        try {
            if (isOnline) {
                return await result.originalRefetch(variables)
            } else {
                return handleOfflineQuery(query, options, localState, result.loading, isOnline, previousData, setPreviousData)
            }
        } catch (error) {
            console.log('error refetch')
        }
    }

    var localResult = handleOfflineQuery(query, options, localState, result.loading, isOnline, previousData, setPreviousData)

    // pass through graphQL query if online
    return cloneDeep(isOnline ? result : localResult)
}

const useOfflineMutation = (mutation, options) => {
    const { localState, setMutatedStorage } = useLocalStorage()
    const { isOnline } = useOnlineUtils()

    const gqlMutation = useBaseMutation(mutation, options)

    // TODO: Save offline data regardless? just in case network issue?
    // TODO: add network detection logic in case they are in the middle of making edits when an error happens
    const getOfflineData = (passedOptions) => {
        let resultData = handleOfflineMutation(mutation, passedOptions, localState, setMutatedStorage)
        let promise = new Promise((resolve, reject) => {
            resolve(resultData)
        })
        return promise
    }

    const localMutation = (passedOptions) => getOfflineData(passedOptions)

    // pass through graphQL mutation if online
    return isOnline ? gqlMutation : [localMutation]
}

const handleOfflineMutation = async (mutation, options, localState, setMutatedStorage) => {
    // write to local mutated state
    // for new entities, record a temp id that will be replaced in the syncing process
    var mutationOperation = mutation.definitions.find(d => d.operation === "mutation")
    var dataKey = mutationOperation.selectionSet.selections[0].name.value // assumes first selection
    let data = {}, id
    const variables = options.variables
    const keys = Object.keys(variables)

    // makes the assumption that some sort of id is necessary for mutation
    const idKey = keys.find(k => k === 'id')
    const otherKey = keys.find(k => k !== 'id')

    var dataToSave = variables[otherKey]

    if (!idKey) {
        // is an edit
        id = createID()
    } else {
        id = variables[idKey]
    }

    dataToSave.id = id
    setMutatedStorage(`${otherKey}:${id}`, dataToSave)
    data[dataKey] = dataToSave


    let resultData = {
        data: data,
        loading: false,
        error: undefined,
        called: undefined,
        client: undefined
    }

    return resultData
}

const handleOfflineQuery = (query, options, localState, loading, isOnline, previousData, setPreviousData) => {
    // decode graphQL query operation to retrieve local state keys
    var queryOperation = query.definitions.find(d => d.operation === "query")
    var selectionDataNames = queryOperation.selectionSet.selections.map(s => s.name.value)
    var data = {}
    const variables = options.variables

    selectionDataNames.forEach(s => {
        if (variables) {
            const keys = Object.keys(variables)
            const state = localState[s] || []

            let res = state.filter(item => 
                keys.every(k => item[k] === variables[k])
            )

            data[s] = keys.includes("id") && res.length > 0 
                ? res[0]
                : res.length === 0 ? localState[s] : res

            // assumes that if there is a variable "id" that a single result comes back
            if (keys.includes("id") && data[s]) {
                let mutation = localState[`${s}:${variables["id"]}`]
                data[s] = mutation || data[s]
            }

        } else {
            data[s] = localState[s]
        }
    })

    // define local return data in form GraphQL recognizes
    let returnData = {
        data: data,
        previousData: undefined,
        loading: false,
        error: undefined,
        variables: options.variables,
        networkStatus: 7,
        refetch: (variables) => {
            return data
        },
        fetchMore: (query, variables) => { },
        startPolling: (number) => { },
        stopPolling: () => { },
        subscribeToMore: (options) => { },
        client: undefined,
        called: undefined,
        useCached: true
    }

    // only call onCompleted once per query
    if (!isOnline && !loading && options.onCompleted) {
        if (!previousData) {
            setPreviousData(data)
            options.onCompleted(data) 
        }
    }

    return returnData
}



export { useOfflineQuery, useOfflineMutation, useOnlineQuery, useQuery, useMutation, useLazyQuery }